import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Send,
  Mic,
  Square,
  Paperclip,
  Loader2,
  ListChecks,
  Trash2,
  Bot,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/constants";
import { Button } from "@/components/ui/button";

const SUGGESTIONS = [
  "Summarise our last standup into tasks",
  "Add 'fix the broken search bar' to the to-do list",
  "What should we prioritise this week?",
];

export function AIChat({ active, onTasksChanged, title = "AI Meeting Assistant", className = "", testId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState("");
  const [recording, setRecording] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const atBottomRef = useRef(true);

  useEffect(() => {
    if (active) {
      atBottomRef.current = true;
      api.get("/ai/history").then((r) => setMessages(r.data)).catch((err) => console.error("Failed to load AI history:", err));
    }
  }, [active]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  useEffect(() => {
    if (atBottomRef.current) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, status]);

  const pushLocal = (role, content) =>
    setMessages((m) => [...m, { role, content, _local: true, created_at: new Date().toISOString() }]);

  const sendToAssistant = async (message, source = "chat") => {
    setSending(true);
    setStatus("Thinking…");
    atBottomRef.current = true;
    try {
      const r = await api.post("/ai/message", { message, source });
      const hist = await api.get("/ai/history");
      setMessages(hist.data);
      if (r.data.created_tasks?.length) {
        toast.success(`${r.data.created_tasks.length} task(s) added to the to-do list`);
        onTasksChanged?.();
      }
      if (r.data.updated_tasks?.length) {
        toast.success(`Updated ${r.data.updated_tasks.length} task(s)`);
        onTasksChanged?.();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Assistant failed");
    } finally {
      setSending(false);
      setStatus("");
    }
  };

  const submitText = (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setText("");
    pushLocal("user", t);
    sendToAssistant(t, "chat");
  };

  const transcribeBlob = async (blob, filename) => {
    setSending(true);
    setStatus("Transcribing your voice note…");
    const form = new FormData();
    form.append("audio", blob, filename);
    try {
      const r = await api.post("/ai/transcribe", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const transcript = r.data.transcript;
      pushLocal("user", `🎙️ ${transcript}`);
      setStatus("Turning it into notes & tasks…");
      await sendToAssistant(transcript, "voice");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not transcribe audio");
      setSending(false);
      setStatus("");
    }
  };

  const onFile = (f) => {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error("Audio too large (max 25MB)");
      return;
    }
    transcribeBlob(f, f.name || "upload.m4a");
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mime = "audio/webm;codecs=opus";
      if (!window.MediaRecorder?.isTypeSupported?.(mime)) {
        mime = MediaRecorder.isTypeSupported?.("audio/webm") ? "audio/webm" : "";
      }
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => ev.data.size > 0 && chunksRef.current.push(ev.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        transcribeBlob(blob, "recording.webm");
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied. You can upload a file instead.");
    }
  };

  const stopRecording = () => {
    mediaRef.current?.stop();
    setRecording(false);
  };

  const clearChat = async () => {
    await api.post("/ai/clear").catch(() => {});
    setMessages([]);
  };

  return (
    <div className={`flex flex-col min-h-0 ${className}`} data-testid={testId}>
      {/* Header */}
      <div className="px-4 sm:px-5 py-3 border-b border-[#2E3242] shrink-0 flex items-center justify-between">
        <div className="font-display flex items-center gap-2 text-base text-gray-100">
          <span className="w-8 h-8 rounded-lg ruby-gradient flex items-center justify-center">
            <Bot className="w-4 h-4 text-white" />
          </span>
          {title}
        </div>
        {messages.length > 0 && (
          <button
            data-testid="ai-clear-button"
            onClick={clearChat}
            className="text-gray-500 hover:text-red-400 flex items-center gap-1 text-xs"
          >
            <Trash2 className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-4"
        data-testid="ai-messages"
      >
        {messages.length === 0 && !sending && (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="w-14 h-14 rounded-2xl ruby-gradient flex items-center justify-center mb-4 ember-glow">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <p className="font-display font-bold text-lg text-gray-100">Ask your AI teammate anything</p>
            <p className="text-sm text-gray-500 mt-1 max-w-sm">
              Ask questions, or tell me what to add to the to-do list — I'll create the trackable
              tasks for the whole team. You can also record or upload a voice note.
            </p>
            <div className="mt-5 flex flex-col gap-2 w-full max-w-sm">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={s}
                  data-testid={`ai-suggestion-${i}`}
                  onClick={() => {
                    pushLocal("user", s);
                    sendToAssistant(s, "chat");
                  }}
                  className="text-left text-sm text-gray-300 bg-[#1E2029] hover:bg-[#262936] border border-[#2E3242] hover:border-red-900/40 rounded-lg px-3 py-2 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <motion.div
              key={m.id || `${m.role}-${i}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}
              data-testid={`ai-msg-${m.role}-${i}`}
            >
              {m.role === "assistant" ? (
                <span className="w-8 h-8 rounded-lg ruby-gradient flex items-center justify-center shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </span>
              ) : (
                <img src={user?.avatar} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                  m.role === "user"
                    ? "ruby-gradient text-white rounded-tr-sm"
                    : "bg-[#1E2029] border border-[#2E3242] text-gray-200 rounded-tl-sm"
                }`}
              >
                {m.content}
                {m.created_tasks?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-emerald-900/40 flex items-center gap-1.5 text-[11px] text-emerald-400">
                    <ListChecks className="w-3.5 h-3.5" />
                    {m.created_tasks.length} task(s) posted to the to-do list
                  </div>
                )}
                {m.updated_tasks?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-blue-900/40 flex items-center gap-1.5 text-[11px] text-blue-400">
                    <ListChecks className="w-3.5 h-3.5" />
                    Updated {m.updated_tasks.length} task(s)
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {sending && (
          <div className="flex gap-2.5">
            <span className="w-8 h-8 rounded-lg ruby-gradient flex items-center justify-center shrink-0">
              <Bot className="w-4 h-4 text-white" />
            </span>
            <div className="bg-[#1E2029] border border-[#2E3242] rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm text-gray-400 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-red-400" />
              {status || "Thinking…"}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Recording banner */}
      {recording && (
        <div className="px-4 sm:px-5 py-2 bg-red-950/50 border-t border-red-900/40 flex items-center justify-between shrink-0">
          <span className="flex items-center gap-2 text-sm text-red-300">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 pulse-glow" /> Recording… speak now
          </span>
          <Button
            data-testid="ai-stop-record-button"
            onClick={stopRecording}
            size="sm"
            className="bg-red-600 hover:bg-red-500 text-white h-8"
          >
            <Square className="w-3.5 h-3.5 mr-1.5" /> Stop & transcribe
          </Button>
        </div>
      )}

      {/* Input */}
      <form onSubmit={submitText} className="p-3 border-t border-[#2E3242] bg-[#121318] shrink-0">
        <input
          ref={fileRef}
          type="file"
          accept="audio/*,.mp3,.wav,.m4a,.webm,.mp4,.mpga,.mpeg"
          className="hidden"
          data-testid="ai-file-input"
          onChange={(e) => {
            onFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <div className="flex items-end gap-2">
          <button
            type="button"
            data-testid="ai-upload-button"
            onClick={() => fileRef.current?.click()}
            disabled={sending || recording}
            className="w-10 h-10 shrink-0 rounded-xl bg-[#1E2029] border border-[#2E3242] text-gray-400 hover:text-white hover:border-red-900/40 flex items-center justify-center disabled:opacity-40 transition-colors"
            title="Upload a recording"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <button
            type="button"
            data-testid="ai-record-button"
            onClick={recording ? stopRecording : startRecording}
            disabled={sending}
            className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center disabled:opacity-40 transition-colors border ${
              recording
                ? "bg-red-600 border-red-500 text-white"
                : "bg-[#1E2029] border-[#2E3242] text-gray-400 hover:text-white hover:border-red-900/40"
            }`}
            title="Record your voice"
          >
            {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          <textarea
            data-testid="ai-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) submitText(e);
            }}
            rows={1}
            placeholder="Ask a question, or tell me what to add to the to-do list…"
            disabled={sending}
            className="flex-1 resize-none bg-[#16181F] border border-[#2E3242] rounded-xl px-3.5 py-2.5 text-sm text-gray-200 placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-red-600 max-h-32 disabled:opacity-60"
          />
          <Button
            type="submit"
            data-testid="ai-send-button"
            disabled={!text.trim() || sending}
            className="w-10 h-10 shrink-0 p-0 ruby-gradient text-white border border-red-900/40"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}

export default AIChat;
